import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import viLocale from "date-fns/locale/vi";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { withStyles } from "@mui/styles";
import { colors } from "src/colors/colors";
import CalenderIcon from "../../assets/iconsSvg/calender";
import { FontSize } from "src/const/font";
import { isCheckDateSpecial } from "../../utils/checkDateSpecial";
import moment from "moment";
import DateFns from 'src/utils/DateFns';
class CustomString extends String {
  charAt(_: number): string {
    return this.valueOf();
  }
}
const weekDays = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const customWeekDays = weekDays.map((day) => new CustomString(day) as string);

class CustomizeDateAdapter extends AdapterDateFns {
  getWeekdays = (): string[] => customWeekDays;
}

interface Props<T extends FieldValues> extends Omit<TextFieldProps, "name"> {
  control: Control<T>;
  name: FieldPath<T>;
  mask?: string;
  onChangeSelect?: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  heightInput?: number;
}
const CssTextField = withStyles((theme) => ({
  root: {
    "& .MuiInputBase-root": {
      borderRadius: "17px",
      paddingRight: "0",
      fontSize: FontSize.normal.default,
      [theme.breakpoints.down("md")]: {
        fontSize: FontSize.normal.p4,
      },
      "& .MuiInputBase-input": {
        padding: "14px 8px 14px 20px",
      },
      "& .MuiButtonBase-root": {
        background: "#E5FBFF",
        borderRadius: "0 16px 16px 0",
        padding: "0px 15px 0px 15px",
        zIndex: 2,
        right: "1px",
        color: colors.text.blueMain,
        marginRight: "0",
        height: (props) => Number(props.heightInput) - 3,
        [theme.breakpoints.down("sm")]: {
          height: (props) => Number(props.heightInput) - 3,
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E0E0E0",
      },
      "&.Mui-focused fieldset": {
        borderWidth: "1px",
      },
    },
  },
}))(TextField);
const DatePickerCustom = <T extends FieldValues>(props: Props<T>) => {
  const {
    control,
    name,
    disabled,
    mask,
    onChangeSelect,
    minDate,
    maxDate,
    heightInput,
    ...rest
  } = props;
  const currentYear = new Date().getFullYear();
  const minYear = 1899;
  const setDateDefaultTime = (date: any) => {
    const newDateDefaultTime = DateFns.addHours(date, 7).setUTCHours(
      0,
      0,
      0,
      0
    );
    return new Date(newDateDefaultTime).toISOString();
  };
  return (
    <Controller
      render={({ field: { ref, ...others }, fieldState: { error } }) => (
        <LocalizationProvider
          dateAdapter={CustomizeDateAdapter}
          locale={viLocale}
        >
          <DesktopDatePicker
            inputFormat="dd/MM/yyyy"
            renderInput={(props: any) => (
              <CssTextField
                {...props}
                {...rest}
                fullWidth
                ref={ref}
                error={Boolean(error)}
                helperText={error?.message && error.message}
                id={name}
                InputLabelProps={{
                  shrink: true,
                }}
                autoComplete={"off"}
                onChange={(e: any) => {
                  props.inputProps.onChange(e);
                }}
                onBlur={(e) => {
                  others.onBlur();
                  const value = e.target.value;
                  const checkDate = () => {
                    const date = moment(value, "DD-MM-YYYY");
                    const convertDateYYYYMMDD = date.format("YYYY/MM/DD");
                    const checkValidDate =
                      isCheckDateSpecial(convertDateYYYYMMDD);
                    const convertDateToArray = convertDateYYYYMMDD
                      .replaceAll("/", ",")
                      .split(",");
                    const year = Number(convertDateToArray[0]);
                    if (
                      checkValidDate &&
                      year > minYear &&
                      year < currentYear + 1
                    ) {
                      others.onChange(convertDateYYYYMMDD as any);
                    } else {
                      const valueConvert = value?.split("/");
                      const convertYYYYMMDD = valueConvert?.reverse()
                      value.length > 0 &&
                        others.onChange(`${convertYYYYMMDD},validdob` as any);
                    }
                  };
                  if (
                    value.length > 0 &&
                    value.length <= 9 &&
                    value.length < 10
                  ) {
                    const valueConvert = value?.split("/");
                    const convertYYYYMMDD = valueConvert?.reverse()
                    others.onChange(`${convertYYYYMMDD},validdob` as any);
                  } else {
                    checkDate();
                  }
                }}
                heightInput={heightInput}
              />
            )}
            mask={mask}
            InputAdornmentProps={{
              position: "end",
            }}
            {...others}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(value: Date | null, keyboardInputValue?: string) => {
              if (keyboardInputValue) {
                const spl = keyboardInputValue?.split("/");
                if (
                  spl &&
                  spl[0]?.length === 2 &&
                  spl[1]?.length === 2 &&
                  spl[2]?.length === 4 &&
                  `${value}` == "Invalid Date"
                ) {
                  const converted = spl?.reverse();
                  const convertDate = isCheckDateSpecial(converted?.join("/"));
                  convertDate && converted[0] !== "0000"
                    ? others.onChange(converted?.join(",") as any)
                    : others.onChange(`${converted?.join(",")},dateValid` as any);
                } else {
                  const year =
                    keyboardInputValue?.length === 10 ? Number(spl[2]) : null;
                  const converted = spl?.reverse();
                  const valueLength: any = keyboardInputValue?.length;
                  if ((year && year <= minYear) || year > currentYear) {
                    others.onChange(`${converted},validdob` as any);
                  } else {
                    if (year) {
                      others.onChange(value as any);
                    } else if (!valueLength) {
                      others.onChange(null as any);
                    }
                  }
                  if (onChangeSelect) {
                    onChangeSelect(value);
                  }
                }
              } else {
                const tempVal =
                  value && `${value}` !== "Invalid Date"
                    ? setDateDefaultTime(new Date(value))
                    : value;
                others.onChange(tempVal as any);
              }
            }}
            components={{
              OpenPickerIcon: CalenderIcon,
            }}
          />
        </LocalizationProvider>
      )}
      name={name}
      control={control}
    />
  );
};

export default DatePickerCustom;
